import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "../../../assets/css/gamesCss/Tree/binaryTreeSearching.css"
import { Col } from "reactstrap"
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import { faL } from "@fortawesome/free-solid-svg-icons";
import { useDarkMode } from "context/DarkModeProvider";
import { useLanguage } from "context/LanguageProvider";
import "../../../assets/css/colors.css"

const BinaryTreeSearch = () => {

    const selectedStage = JSON.parse(localStorage.getItem("selectedStage"));
    const { selLanguage, toggleLanguage, isLoading } = useLanguage();
    var games = selectedStage.games;
    var nameGameKey = localStorage.getItem("gameClicked");
    var selectedGame = null;
    for (let i = 0; i < games.length; i++) {
        if (games[i].game_title === nameGameKey) {
            selectedGame = games[i];
            break;
        }
    }
    const [currentStage, setCurrentStage] = useState(1);
    const [targetValue, setTargetValue] = useState(null);
    const [searchPath, setSearchPath] = useState([]);
    const [message, setMessage] = useState("");
    const [gameState, setGameState] = useState("playing");
    const [lives, setLives] = useState(3);
    const [showHint, setShowHint] = useState(false);
    const [wrongAttempts, setWrongAttempts] = useState(0);
    const [clickedNodes, setClickedNodes] = useState(new Set());
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const { darkMode } = useDarkMode();
    const containerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);

    const availableWidth = containerWidth || 1000;
    const minNodeSpacing = 60;
    const maxNodeSpacing = 100;


    const handlePlayAgain = () => {
        resetGame();
    };

    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };
        updateWidth();
        window.addEventListener('resize', updateWidth);
    }, []);

    const stages = {
        1: {

            tree: [50, 30, 70, 20, 40, 60, 80],
            treeSize: 7,
            searchTargets: [40, 80, 20],
            hints: [
                "Ξεκινήστε από τη ρίζα (50) και συγκρίνετε με τον στόχο",
                "Αν ο στόχος < τρέχον κόμβο, πηγαίνετε αριστερά",
                "Αν ο στόχος > τρέχον κόμβο, πηγαίνετε δεξιά"
            ]
        },
        2: {

            tree: [100, 50, 150, 25, 75, 125, 175, 10, 35, 60, 90, 110, 140, 160, 200],
            treeSize: 15,
            searchTargets: [90, 140, 35],
            hints: [
                "Ξεκινήστε πάντα από τη ρίζα του δέντρου",
                "Κάθε σύγκριση σας οδηγεί σε μία κατεύθυνση",
                "Το μονοπάτι σας πρέπει να ακολουθεί τη λογική BST"
            ]
        },
        3: {

            tree: [128, 64, 192, 32, 96, 160, 224, 16, 48, 80, 112, 144, 176, 208, 240, 8, 24, 40, 56, 72, 88, 104, 120, 136, 152, 168, 184, 200, 216, 232, 248],
            treeSize: 31,
            searchTargets: [88, 152, 216],
            hints: [
                "Μεγάλο δέντρο = χρειάζεστε στρατηγική!",
                "Κάθε κίνηση μισό-διπλώνει τις επιλογές σας",
                "Ακολουθήστε το BST pattern: αριστερά για μικρότερα, δεξιά για μεγαλύτερα"
            ]
        }
    };

    useEffect(() => {
        initializeStage(currentStage);
    }, [currentStage]);

    const initializeStage = (stage) => {
        const stageData = stages[stage];
        const randomTarget = stageData.searchTargets[Math.floor(Math.random() * stageData.searchTargets.length)];
        setTargetValue(randomTarget);
        setSearchPath([]);
        setClickedNodes(new Set());
        setGameState("playing");
        setShowHint(false);
        setWrongAttempts(0);
    };

    const getCorrectPath = (target, tree) => {
        const path = [];
        let currentIndex = 0;

        while (currentIndex < tree.length && tree[currentIndex] !== null) {
            const currentValue = tree[currentIndex];
            path.push(currentValue);

            if (currentValue === target) {
                break;
            } else if (target < currentValue) {
                currentIndex = 2 * currentIndex + 1; // αριστερό παιδί
            } else {
                currentIndex = 2 * currentIndex + 2; // δεξί παιδί
            }
        }

        return path;
    };

    const handleNodeClick = (nodeValue, nodeIndex) => {
        if (gameState !== "playing") return;

        const newClickedNodes = new Set(clickedNodes);
        const newPath = [...searchPath];

        // Έλεγχος αν ο κόμβος είναι ήδη επιλεγμένος
        const existingIndex = newPath.indexOf(nodeValue);

        if (existingIndex !== -1) {
            // Αφαιρούμε τον κόμβο
            newPath.splice(existingIndex, 1);

            // Ενημέρωση clickedNodes
            newClickedNodes.delete(nodeIndex);
        } else {
            // Προσθέτουμε τον κόμβο στο τέλος
            newPath.push(nodeValue);
            newClickedNodes.add(nodeIndex);
        }

        setSearchPath(newPath);
        setClickedNodes(newClickedNodes);
    };


    const checkSolution = (path = searchPath) => {
        if (path.length === 0) return;

        const correctPath = getCorrectPath(targetValue, stages[currentStage].tree);
        const isCorrect = JSON.stringify(path) === JSON.stringify(correctPath);

        if (isCorrect) {
            if (currentStage === 3) {
                setGameState("completed");
                setShowGameWon(true);
                setMessage(selLanguage ? " Συγχαρητήρια! Ολοκληρώσατε όλα τα στάδια με επιτυχία!" : " Congratulations! You've completed all stages successfully!");
            } else {
                setMessage(selLanguage ? ` Τέλεια! Βρήκατε το ${targetValue} σε ${path.length} βήματα! Προχωρώντας στο στάδιο ${currentStage + 1}` : ` Perfect! You found ${targetValue} in ${path.length} steps! Moving to stage ${currentStage + 1}`);
                setTimeout(() => {
                    setCurrentStage(currentStage + 1);
                }, 3000);
            }
            setShowHint(false);
            setWrongAttempts(0);
        } else {
            // Διόρθωση: Υπολογίζουμε τις νέες ζωές πριν τις ορίσουμε
            const newLives = lives - 1;
            setLives(newLives);
            setWrongAttempts(wrongAttempts + 1);
            setShowHint(true);

            if (newLives === 0) {
                setGameState("lost");
                setShowGameOver(true);
                setMessage(selLanguage ? `Έχασες όλες τις ζωές σου! Η σωστή διαδρομή ήταν: ${correctPath.join(' → ')}` : ` You've lost all your lives! The correct path was: ${correctPath.join(' → ')}`);
            } else {
                setMessage(selLanguage ? ` Λάθος διαδρομή! Απομένουν ${newLives} ζωές. Η διαδρομή σας: ${path.join(' → ')}` : ` Wrong path! You have ${newLives} lives left. Your path: ${path.join(' → ')}`);
            }
        }
    };

    const resetGame = () => {
        setCurrentStage(1);
        setLives(3);
        setGameState("playing");
        setMessage("");
        setShowHint(false);
        setWrongAttempts(0);
        setShowGameOver(false);
        setShowGameWon(false);
    };

    const resetCurrentStage = () => {
        initializeStage(currentStage);
    };

    const getHint = () => {
        const stageData = stages[currentStage];
        const hintIndex = Math.min(wrongAttempts, stageData.hints.length - 1);
        const correctPath = getCorrectPath(targetValue, stageData.tree);

        let hint = stageData.hints[hintIndex];

        if (wrongAttempts >= 2) {
            const nextStep = searchPath.length < correctPath.length ? correctPath[searchPath.length] : null;
            if (nextStep) {
                hint += selLanguage ? ` Επόμενο βήμα: ${nextStep}` : ` Next step: ${nextStep}`;
            }
        }

        return hint;
    };

    const TREE_TOP_OFFSET = 20; // λίγα px κενό από πάνω

    const getNodePositions = (level, nodeIndex) => {
    const tree = stages[currentStage].tree;

    const maxLevel = Math.floor(Math.log2(tree.length));
    
    const nodeSpacing = currentStage === 1 ? 50 : currentStage === 2 ? 65 : 50;
    const maxNodesLastLevel = Math.pow(2, maxLevel);
    const baseWidth = maxNodesLastLevel * nodeSpacing;

    // ΚΑΘΕΤΟ SPACING ΑΝΑ STAGE
    const verticalSpacing = currentStage === 1 ? 70 : 80;

    const levelSpacing = baseWidth / Math.pow(2, level);
    const nodesInLevel = Math.pow(2, level);
    const indexInLevel = nodeIndex - (Math.pow(2, level) - 1);
    const startOffset = -(nodesInLevel - 1) * levelSpacing / 2;

    return {
        x: startOffset + indexInLevel * levelSpacing,
        y: level * verticalSpacing + TREE_TOP_OFFSET
    };
};


    // Υπολογίζει ποιο επίπεδο ανήκει ένας κόμβος
    const getNodeLevel = (index) => {
        return Math.floor(Math.log2(index + 1));
    };

    // Δημιουργεί τις ακμές του δέντρου
    const renderEdges = () => {
        const edges = [];
        const tree = stages[currentStage].tree;

        for (let i = 0; i < tree.length; i++) {
            if (tree[i] === null) continue;

            const leftChildIndex = 2 * i + 1;
            const rightChildIndex = 2 * i + 2;

            const parentLevel = getNodeLevel(i);
            const parentPos = getNodePositions(parentLevel, i);

            // Αριστερό παιδί
            if (leftChildIndex < tree.length && tree[leftChildIndex] !== null) {
                const childLevel = getNodeLevel(leftChildIndex);
                const childPos = getNodePositions(childLevel, leftChildIndex);


                edges.push(
                    <line
                        key={`edge-${i}-${leftChildIndex}`}
                        x1={parentPos.x}
                        y1={parentPos.y + 25}
                        x2={childPos.x}
                        y2={childPos.y + 25}
                        stroke="#64748b"
                        strokeWidth="2"
                        opacity="0.6"
                    />
                );
            }

            // Δεξί παιδί
            if (rightChildIndex < tree.length && tree[rightChildIndex] !== null) {
                const childLevel = getNodeLevel(rightChildIndex);
                const childPos = getNodePositions(childLevel, rightChildIndex, 5);

                edges.push(
                    <line
                        key={`edge-${i}-${rightChildIndex}`}
                        x1={parentPos.x}
                        y1={parentPos.y + 25}
                        x2={childPos.x}
                        y2={childPos.y + 25}
                        stroke="#64748b"
                        strokeWidth="2"
                        opacity="0.6"
                    />
                );
            }
        }

        return edges;
    };

    const renderNode = (nodeValue, index) => {
        if (nodeValue === null) return null;

        const isClicked = clickedNodes.has(index);
        const clickOrder = searchPath.indexOf(nodeValue) + 1;
        const level = getNodeLevel(index);
        const position = getNodePositions(level, index);


        return (
            <g key={index}>
                <motion.circle
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    onClick={() => handleNodeClick(nodeValue, index)}
                    cx={position.x}
                    cy={position.y + 25}
                    // ΠΙΟ ΜΙΚΡΟ ΣΤΟ STAGE 1
                    r={currentStage === 1 ? "14" : currentStage === 3 ? "18" : "25"}
                    fill={isClicked ? "#3b82f6" : "#64748b"}
                    stroke="rgba(255,255,255,0.1)"
                    strokeWidth="2"
                    style={{
                        cursor: gameState === "playing" ? "pointer" : "default",
                        filter: isClicked
                        ? "drop-shadow(0 6px 15px rgba(59, 130, 246, 0.4))"
                        : "drop-shadow(0 4px 8px rgba(0,0,0,0.15))",
                        transition: "all 0.3s ease"
                    }}
                    />

                    <text
                    x={position.x}
                    y={position.y + 30}
                    textAnchor="middle"
                    fill="white"
                    // ΠΙΟ ΜΙΚΡΗ ΓΡΑΜΜΑΤΟΣΕΙΡΑ ΣΤΟ STAGE 1
                    fontSize={currentStage === 1 ? "10" : currentStage === 3 ? "11" : "14"}
                    fontWeight="bold"
                    style={{ pointerEvents: "none" }}
                    >
                    {nodeValue}
                    </text>


            {clickOrder > 0 && (
                <g>
                    <circle
                        cx={position.x + (currentStage === 1 ? 15 : currentStage === 3 ? 15 : 20)}
                        cy={position.y + (currentStage === 1 ? 8 : currentStage === 3 ? 8 : 5)}
                        r={currentStage === 1 ? "8" : currentStage === 3 ? "8" : "10"}
                        fill="#ef4444"
                    />
                    <text
                        x={position.x + (currentStage === 1 ? 15 : currentStage === 3 ? 15 : 20)}
                        y={position.y + (currentStage === 1 ? 11 : currentStage === 3 ? 11 : 9)}
                        fontSize={currentStage === 1 ? "10" : currentStage === 3 ? "10" : "12"}
                        fill="white"                        
                        fontWeight="bold"
                        style={{ pointerEvents: "none" }}
                    >
                        {clickOrder}
                    </text>
                </g>
            )}
            </g>
        );
    };

    const renderTree = () => {
    const tree = stages[currentStage].tree;
    const maxLevel = Math.floor(Math.log2(tree.length));

    const nodeSpacing = currentStage === 1 ? 50 : currentStage === 2 ? 65 : 50;
    const nodesLastLevel = Math.pow(2, maxLevel);
    const baseWidth = nodesLastLevel * nodeSpacing;

    // Ίδιο κάθετο spacing με το getNodePositions
    const verticalSpacing = currentStage === 1 ? 70 : 80;

    // ΠΙΟ ΜΙΚΡΟ ΥΨΟΣ, ΧΩΡΙΣ ΤΟΣΟ ΠΟΛΛΟ EXTRA ΚΕΝΟ
    const svgHeight = (maxLevel + 1) * verticalSpacing + 80 + TREE_TOP_OFFSET; ;

    const totalWidth = baseWidth + 150;
    const viewBoxX = -totalWidth / 2;

    return (
        <div style={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
            marginTop: "30px"
        }}>
            <svg
                width="100%"
                viewBox={`${viewBoxX} 0 ${totalWidth} ${svgHeight}`}
                preserveAspectRatio="xMidYMid meet"
            >
                {renderEdges()}
                {tree.map((nodeValue, index) =>
                    nodeValue !== null ? renderNode(nodeValue, index) : null
                )}
            </svg>
        </div>
    );
};


    useEffect(() => {
        if (message && !message.includes(selLanguage ? "Βρείτε" : "Find")) {
            const timer = setTimeout(() => {
                setMessage("");
            }, 4000);
            return () => clearTimeout(timer);
        }
    }, [message]);

    return (
        <div
            className='main-content binaryTreeContainer'
            style={{ background: darkMode ? "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #101724ff 100%)" : "var(--lightModeGameBackground)" }}
        >
            <GameOverModal
                isVisible={showGameOver}
                onPlayAgain={handlePlayAgain}

            />

            <GameWonModal
                isVisible={showGameWon}
                onPlayAgain={handlePlayAgain}

            />

            <Col xl="12" lg="12" md="12" sm="12" xs="12">
                <div
                    className="max-w-7xl mx-auto"
                    style={{
                        width: "100%",
                        background: darkMode ? "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #101724ff 100%)" : "var(--lightModeGameBackground)"

                    }}
                >

                    {/* Lives και Stage Info */}
                    <div style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "15px",
                        flexWrap: "wrap",
                        gap: "10px",
                        marginTop: "15px"
                    }}>
                        <div style={{
                            color: darkMode ? "white" : "#080808bb",
                            padding: "12px 24px",
                            borderRadius: "12px",
                            fontWeight: "700",
                            fontSize: "18px",
                            border: darkMode ? "2px solid rgba(255, 255, 255, 0.2)" : "2px solid #131212bb",
                            display: "flex",
                            alignItems: "center",
                            gap: "10px"
                        }}>
                            {selLanguage ? `Στάδιο ${currentStage}/3` : `Stage ${currentStage}/3`}
                        </div>

                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                            marginRight: "10px"
                        }}>

                            <div style={{ display: "flex", gap: "6px" }}>
                                {[1, 2, 3].map((heartNum) => (
                                    <div
                                        key={heartNum}
                                        style={{
                                            fontSize: "24px",
                                            filter: lives >= heartNum
                                                ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.4))"
                                                : "grayscale(100%) opacity(0.3)",
                                        }}
                                    >
                                        ❤️
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Description */}
                    <div className="binaryTreePron">
                        {selLanguage ? `Αναζητήστε τον κόμβο: ${targetValue}` : `Search for the node: ${targetValue}`}
                    </div>



                    {/* Tree Visualization */}
                    <div className="binaryTreeVisual">
                        {renderTree()}
                    </div>



                    {/* Messages */}
                    <AnimatePresence>
                        {message && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                style={{
                                    textAlign: "center",
                                    padding: "20px",
                                    borderRadius: "16px",
                                    marginBottom: "20px",
                                    background: message.includes("Σωστά") || message.includes("Correct") || message.includes("Τέλεια") || message.includes("Great") || message.includes("Συγχαρητήρια") || message.includes("Congratulations")
                                        ? "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)"
                                        : message.includes("Λάθος") || message.includes("Wrong") || message.includes("Έχασες") || message.includes("lost") || message.includes("Απομένουν") || message.includes("lives left")
                                            ? "linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)"
                                            : "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
                                    border: `2px solid ${message.includes("Σωστά") || message.includes("Correct") || message.includes("Τέλεια") || message.includes("Great") || message.includes("Συγχαρητήρια") || message.includes("Congratulations") ? "#10b981" :
                                        message.includes("Λάθος") || message.includes("Wrong") || message.includes("Έχασες") || message.includes("lost") || message.includes("Απομένουν") || message.includes("lives left") ? "#ef4444" : "#3b82f6"
                                        }`,
                                    color: message.includes("Σωστά") || message.includes("Correct") || message.includes("Τέλεια") || message.includes("Great") || message.includes("Συγχαρητήρια") || message.includes("Congratulations") ? "#059669" :
                                        message.includes("Λάθος") || message.includes("Wrong") || message.includes("Έχασες") || message.includes("lost") || message.includes("Απομένουν") || message.includes("lives left") ? "#dc2626" : "#1d4ed8",
                                    fontWeight: 600,
                                    fontSize: "1rem",
                                    maxWidth: "700px",
                                    margin: "0 auto 20px auto",
                                    width: "90%"
                                }}
                            >
                                {message}
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Buttons */}
                    <div className="binaryTreeButtonsSpace">
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => checkSolution()}
                            disabled={gameState !== "playing" || searchPath.length === 0}
                            style={{
                                background: (gameState === "playing" && searchPath.length > 0)
                                    ? "linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)"
                                    : "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)",
                                color: "white",
                                border: "none",
                                borderRadius: "16px",
                                padding: "14px 28px",
                                fontSize: "1rem",
                                fontWeight: 600,
                                cursor: (gameState === "playing" && searchPath.length > 0) ? "pointer" : "not-allowed",
                                boxShadow: "0 10px 25px rgba(59, 130, 246, 0.3)",
                                transition: "all 0.3s ease",
                                flex: 1,
                                maxWidth: "150px"
                            }}
                        >
                            {selLanguage ? "Υποβολή" : "Submit"}
                        </motion.button>

                        <motion.button
                            className="binaryTreeReset"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={resetCurrentStage}
                        >
                            {selLanguage ? "Επαναφορά" : "Reset"}
                        </motion.button>

                        {(gameState === "lost" || gameState === "completed") && (
                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={resetGame}
                                style={{
                                    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                    color: "white",
                                    border: "none",
                                    borderRadius: "16px",
                                    padding: "14px 28px",
                                    fontSize: "1rem",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    boxShadow: "0 10px 25px rgba(16, 185, 129, 0.3)",
                                    transition: "all 0.3s ease",
                                    flex: 1,
                                    maxWidth: "150px"
                                }}
                            >
                                {selLanguage ? "Νέο Παιχνίδι" : "New Game"}
                            </motion.button>
                        )}
                    </div>
                </div>
            </Col>
        </div>
    );
};

export default BinaryTreeSearch;